import type { CSSProperties } from 'react';
import type { TrackStyle } from '@core/styles/stylePresets.ts';
import {
  boxShadowFor,
  parseBackgroundColor,
  textShadowFor,
} from '@core/styles/applyTrackStyle.ts';

export type StyleToCssOptions = {
  viewHeight: number;
  maxWidthPx?: number;
};

/**
 * TrackStyle → React CSSProperties (ported from paintTrackStyle).
 * fontSize = viewHeight * 0.045 * fontSizeRatio
 */
export function styleToCss(
  style: TrackStyle,
  options: StyleToCssOptions,
): CSSProperties {
  const fontSize = options.viewHeight * 0.045 * style.fontSizeRatio;
  const css: CSSProperties = {
    fontSize: `${fontSize}px`,
    fontFamily: style.fontFamily,
    fontWeight: style.fontWeight,
    color: style.fontColor,
    letterSpacing: `${style.letterSpacing}em`,
    lineHeight: style.lineHeight,
    padding: `${style.padding}px`,
    opacity: style.opacity,
    backgroundColor: parseBackgroundColor(
      style.fontBackgroundColor,
      style.fontBackgroundColorOpacity,
    ),
    whiteSpace: 'pre-wrap',
    textAlign: 'center',
    boxSizing: 'border-box',
  };

  if (options.maxWidthPx !== undefined) {
    css.maxWidth = `${options.maxWidthPx}px`;
  }

  if (style.strokeWidth) {
    css.WebkitTextStrokeWidth = `${style.strokeWidth}px`;
    css.WebkitTextStrokeColor = style.strokeColor;
    // paint-order is non-standard on CSSProperties
    (css as Record<string, string>).paintOrder = 'stroke fill';
  }

  const shadow = textShadowFor(style.shadow, style.shadowColor);
  if (shadow) css.textShadow = shadow;

  const box = boxShadowFor(style.boxShadow);
  if (box) css.boxShadow = box;

  return css;
}

/** Sync Google Fonts stylesheet for a slot (idempotent). */
export function syncGoogleFontLink(
  slot: string,
  families: string,
  cssProperties: string,
  doc: Document = document,
): void {
  const id = `rikosub-google-style-${slot}`;
  const existing = doc.getElementById(id);
  if (!families || !cssProperties) {
    existing?.remove();
    return;
  }
  const textContent =
    `@import url('https://fonts.googleapis.com/css2?${families}');\n` +
    `.rikosub-google-font-${slot} { ${cssProperties} }`;
  if (existing?.textContent === textContent) return;
  existing?.remove();
  const style = doc.createElement('style');
  style.id = id;
  style.textContent = textContent;
  doc.head.appendChild(style);
}
